import { useState, useRef, useEffect } from "react";
import { X, Camera, Images, FolderOpen, Plus, Loader2, ChevronLeft, Link, Trash2 } from "lucide-react";
import axiosInstance from "../../APIs/axiosInstance";

export default function AddPostModal({ onClose }) {
  const [activePage, setActivePage] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [caption, setCaption] = useState("");
  const [links, setLinks] = useState([{ label: "Visit Link", url: "" }]); // ✅ Multiple links
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Revoke object URLs when files are removed or modal unmounts
  useEffect(() => {
    return () => {
      selectedFiles.forEach((f) => URL.revokeObjectURL(f.url));
    };
  }, [selectedFiles]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const previews = files.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      id: Date.now() + Math.random(),
    }));
    setSelectedFiles((prev) => [...prev, ...previews]);
    setActivePage("preview");
  };

  const removeFile = (id) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== id));
    if (selectedFiles.length <= 1) setActivePage(null);
  };

  // ✅ Link handlers
  const addLink = () => {
    if (links.length >= 5) return; // Max 5 links
    setLinks((prev) => [...prev, { label: "", url: "" }]);
  };

  const updateLink = (index, field, value) => {
    setLinks((prev) =>
      prev.map((l, i) => (i === index ? { ...l, [field]: value } : l))
    );
  };

  const removeLink = (index) => {
    setLinks((prev) => prev.filter((_, i) => i !== index));
  };

  // Upload
  const handleUpload = async () => {
    if (!selectedFiles.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("image", selectedFiles[0].file, selectedFiles[0].file.name);
      form.append("caption", caption || "");

      for (let i = 1; i < selectedFiles.length; i++) {
        form.append("additional_images", selectedFiles[i].file, selectedFiles[i].file.name);
      }

      const validLinks = links.filter((l) => l.url.trim());
      if (validLinks.length > 0) {
        form.append("link", validLinks[0].url);
        form.append("links_data", JSON.stringify(validLinks));
      }

      await axiosInstance.post("/posts/", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onClose();
    } catch (err) {
      console.error("Upload error:", err.response?.data ?? err.message);
      alert("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end">
      <div className="bg-[#111] w-full rounded-t-3xl border-t border-white/10 shadow-2xl animate-slideUp">

        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-white/20 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            {activePage && (
              <button onClick={() => setActivePage(null)}
                className="p-1.5 rounded-full hover:bg-white/10 transition">
                <ChevronLeft className="w-4 h-4 text-white/60" />
              </button>
            )}
            <h2 className="text-white font-semibold">
              {activePage === "preview" ? "Preview" : "Create Post"}
            </h2>
          </div>
          <button onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/10 transition">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        <div className="px-5 py-4 max-h-[75vh] overflow-y-auto">

          {/* Source picker */}
          {!activePage && (
            <div className="grid grid-cols-3 gap-3 py-4">
              {[
                { icon: <Camera className="w-7 h-7" />, label: "Camera" },
                { icon: <Images className="w-7 h-7" />, label: "Photos" },
                { icon: <FolderOpen className="w-7 h-7" />, label: "Files" },
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={() => fileRef.current.click()}
                  className="flex flex-col items-center gap-2 py-5
                    bg-white/5 border border-white/10 rounded-2xl
                    hover:bg-white/10 transition text-white/70 hover:text-white"
                >
                  {item.icon}
                  <span className="text-xs font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Preview */}
          {activePage === "preview" && (
            <>
              {/* Images grid */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                {selectedFiles.map((item) => (
                  <div key={item.id}
                    className="relative rounded-xl overflow-hidden aspect-square">
                    <img src={item.url} alt="preview"
                      className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeFile(item.id)}
                      className="absolute top-1 right-1 w-6 h-6 bg-black/60
                        rounded-full flex items-center justify-center
                        hover:bg-red-500/80 transition"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => fileRef.current.click()}
                  className="aspect-square rounded-xl border-2 border-dashed
                    border-white/10 flex items-center justify-center
                    hover:border-white/20 transition"
                >
                  <Plus className="w-6 h-6 text-white/20" />
                </button>
              </div>

              {/* Caption */}
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Write a caption..."
                rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-xl
                  px-4 py-3 text-white text-sm outline-none
                  placeholder-white/20 focus:border-white/20 transition
                  resize-none mb-4"
              />

              {/* ✅ Multiple Links */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white/40 text-xs uppercase tracking-wider
                    flex items-center gap-1.5">
                    <Link className="w-3.5 h-3.5" />
                    Links
                  </p>
                  {links.length < 5 && (
                    <button
                      onClick={addLink}
                      className="text-white/40 hover:text-white text-xs
                        flex items-center gap-1 transition"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Link
                    </button>
                  )}
                </div>

                <div className="space-y-2">
                  {links.map((link, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <div className="flex-1 flex gap-2">
                        <input
                          value={link.label}
                          onChange={(e) => updateLink(i, "label", e.target.value)}
                          placeholder="Label"
                          className="w-24 bg-white/5 border border-white/10
                            rounded-xl px-3 py-2 text-white text-xs outline-none
                            placeholder-white/20 focus:border-white/20 transition"
                        />
                        <input
                          value={link.url}
                          onChange={(e) => updateLink(i, "url", e.target.value)}
                          placeholder="https://..."
                          className="flex-1 bg-white/5 border border-white/10
                            rounded-xl px-3 py-2 text-white text-xs outline-none
                            placeholder-white/20 focus:border-white/20 transition"
                        />
                      </div>
                      {links.length > 1 && (
                        <button
                          onClick={() => removeLink(i)}
                          className="p-1.5 text-white/20 hover:text-red-400
                            transition shrink-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Upload button */}
              <button
                onClick={handleUpload}
                disabled={uploading || !selectedFiles.length}
                className={`w-full py-3 rounded-xl font-medium text-sm
                  flex items-center justify-center gap-2 transition
                  ${uploading || !selectedFiles.length
                    ? "bg-white/5 text-white/20 cursor-not-allowed"
                    : "bg-white text-black hover:bg-gray-100 active:scale-95"
                  }`}
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                ) : (
                  `Post ${selectedFiles.length > 1
                    ? `${selectedFiles.length} Photos`
                    : "Photo"}`
                )}
              </button>
            </>
          )}
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>

      <style>{`
        .animate-slideUp {
          animation: slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1);
        }
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}